// CERBERUS on screen: the Blender model from the bosses thread (Assets/bosses/cerberus/cerberus.glb,
// PR #45) posed from the networked enemy plus the run block's boss state (sim/boss.ts, runwire.ts).
//
// What it shows, every beat readable from across the arena:
//   - the drop-in: it falls out of the sky during its spawn state and slams down
//   - walking: the cab bobs and the legs rock over the tracked feet
//   - the TELL: the attacking head rears back, its eye flares and pulses; the ground shows
//     what's coming (a lunge lane, the bark ring, the scorch fan, a howl dome)
//   - jaws: the head that just attacked hangs its jaw open with a gold weak-point glow
//   - broken heads droop dark and smoke; the furnace door swings open on the core
//   - Static Ruff: a magenta hyper shell over the cab and heads, cracking as the meter fills
//   - death: it buckles forward and sinks while the cab blows out

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { CERBERUS_SCALE } from '../../../sim/defs';
import type { CerberusNet } from '../../../sim/bosses/cerberus';
import { withHaze } from '../materials';

const BASE = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
const MODEL = BASE + 'assets/run/cerberus.glb';
const S = CERBERUS_SCALE;
const HEAD_KEYS = ['furnace', 'dynamo', 'magnet'];
// attack → which head owns it (-1: the body / all of them); matches sim attackHead
const ATK_HEAD = [-1, -1, 2, 0, 1, -1, -1];
const DROP_H = 70;

const DECAL_VERT = /* glsl */ `
varying vec2 vUv;
void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 ); }
`;
// mode 0 lane (u across, v along), 1 ring (annulus at uR), 2 fan (sector over uArc), 3 dome rim
const DECAL_FRAG = /* glsl */ `
varying vec2 vUv;
uniform float uMode; uniform float uK; uniform float uTime; uniform float uR; uniform float uArc; uniform vec3 uCol;
void main() {
  float a = 0.0;
  if ( uMode < 0.5 ) {
    float edge = 1.0 - smoothstep( 0.4, 0.5, abs( vUv.x - 0.5 ) );
    float rim = smoothstep( 0.36, 0.5, abs( vUv.x - 0.5 ) ) * edge;
    float chev = step( 0.5, fract( vUv.y * 9.0 - abs( vUv.x - 0.5 ) * 2.0 - uTime * 2.2 ) );
    float fill = smoothstep( 0.0, 1.0, uK * 1.15 - vUv.y * 0.15 ) ;
    a = edge * ( 0.18 + 0.32 * chev * fill ) + rim * 0.9;
    a *= smoothstep( 0.0, 0.04, vUv.y ) * smoothstep( 1.0, 0.92, vUv.y );
  } else {
    vec2 c = vUv * 2.0 - 1.0;
    float r = length( c );
    if ( uMode < 1.5 ) {
      a = exp( -pow( ( r - uR ) * 18.0, 2.0 ) ) * 1.2 + step( r, uR ) * 0.06 * smoothstep( uR - 0.3, uR, r );
      a *= step( r, 1.0 );
    } else if ( uMode < 2.5 ) {
      float ang = atan( c.x, -c.y );
      float inside = step( abs( ang ), uArc ) * step( r, 1.0 ) * step( 0.1, r );
      float rim = ( exp( -pow( ( r - 1.0 ) * 30.0, 2.0 ) ) + exp( -pow( ( abs( ang ) - uArc ) * r * 30.0, 2.0 ) ) ) * step( abs( ang ), uArc + 0.02 ) * step( r, 1.02 );
      float stripes = step( 0.5, fract( r * 7.0 - uTime * 1.6 ) );
      a = inside * ( 0.12 + 0.28 * stripes * uK ) + rim * 0.9;
    } else {
      a = exp( -pow( ( r - 1.0 ) * 10.0, 2.0 ) ) * step( r, 1.05 ) * ( 0.5 + 0.5 * sin( uTime * 14.0 ) );
    }
  }
  gl_FragColor = vec4( uCol * a, a );
}
`;

const SHELL_VERT = /* glsl */ `
varying vec3 vN; varying vec3 vV; varying vec3 vP;
void main() {
  vec4 wp = modelMatrix * vec4( position, 1.0 );
  vP = position;
  vN = normalize( mat3( modelMatrix ) * normal );
  vV = normalize( cameraPosition - wp.xyz );
  gl_Position = projectionMatrix * viewMatrix * wp;
}
`;
const SHELL_FRAG = /* glsl */ `
varying vec3 vN; varying vec3 vV; varying vec3 vP;
uniform float uTime; uniform float uOn; uniform float uCrack; uniform vec3 uCol;
float h( vec3 p ) { return fract( sin( dot( p, vec3( 12.9898, 78.233, 37.719 ) ) ) * 43758.5453 ); }
void main() {
  float fr = pow( 1.0 - abs( dot( vN, vV ) ), 2.2 );
  // hex-ish cells
  vec3 q = vP * 0.55;
  vec3 cell = floor( q );
  vec3 f = fract( q ) - 0.5;
  float edge = smoothstep( 0.36, 0.5, max( max( abs( f.x ), abs( f.y ) ), abs( f.z ) ) );
  float flick = 0.7 + 0.3 * sin( uTime * 9.0 + h( cell ) * 30.0 );
  // the crack meter: more cells go dark and their edges go white-hot
  float broke = step( h( cell + 3.1 ), uCrack * 0.85 );
  float a = ( fr * 0.75 + edge * 0.35 * flick ) * ( 1.0 - broke * 0.8 ) + broke * edge * 1.2;
  a *= uOn;
  vec3 col = mix( uCol, vec3( 1.0, 0.9, 1.0 ), broke * edge );
  gl_FragColor = vec4( col * a, a );
}
`;

interface Head {
  boom: THREE.Object3D | null; head: THREE.Object3D | null; jaw: THREE.Object3D | null;
  eye: THREE.Mesh | null; eyeMat: THREE.MeshStandardMaterial | null; eyeBase: THREE.Color;
  mouth: THREE.Object3D | null; glow: THREE.Sprite;
  jawA: number; rear: number; droop: number;
  boomRest: THREE.Euler; headRest: THREE.Euler; jawRest: THREE.Euler;
}

export interface BossDraw {
  x: number; y: number; z: number; yaw: number;
  st: number; prog: number; hp: number;
  aimX: number; aimZ: number;
  hitAge: number;
  net: CerberusNet;
}

export class BossView {
  readonly group = new THREE.Group();
  private model: THREE.Object3D | null = null;
  private body: THREE.Object3D | null = null;
  private legs: THREE.Object3D[] = [];
  private door: THREE.Object3D | null = null;
  private heads: Head[] = [];
  private paints: THREE.MeshStandardMaterial[] = [];
  private shell: THREE.Mesh;
  private shellU: Record<string, THREE.IUniform>;
  private lane: THREE.Mesh; private laneU: Record<string, THREE.IUniform>;
  private ring: THREE.Mesh; private ringU: Record<string, THREE.IUniform>;
  private fan: THREE.Mesh; private fanU: Record<string, THREE.IUniform>;
  private coreLight: THREE.PointLight;
  private visibleAt = -1;
  private lastSeen = -1;
  private walk = 0;
  private lx = 0; private lz = 0;
  private deadAt = -1;
  private deadPose: { x: number; y: number; z: number; yaw: number } | null = null;
  private shellOn = 0;
  private disposables: { dispose(): void }[] = [];
  /** world positions of the open weak points this frame (for the reticle) */
  readonly weak: THREE.Vector3[] = [];

  constructor(scene: THREE.Scene, time: { value: number }, prewarm?: (objs: THREE.Object3D[]) => void) {
    this.group.name = 'cerberus';
    this.group.visible = false;
    scene.add(this.group);
    new GLTFLoader().load(MODEL, (g) => { this.onModel(g.scene); prewarm?.([this.group]); }, undefined, (e) => console.warn('[cerberus] model failed', e));

    // hyper shell: an ellipsoid over the cab and the three heads
    this.shellU = { uTime: time, uOn: { value: 0 }, uCrack: { value: 0 }, uCol: { value: new THREE.Color(1.0, 0.25, 0.85) } };
    const sg = new THREE.SphereGeometry(1, 40, 24);
    const sm = new THREE.ShaderMaterial({ vertexShader: SHELL_VERT, fragmentShader: SHELL_FRAG, uniforms: this.shellU, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    this.shell = new THREE.Mesh(sg, sm);
    this.shell.scale.set(10.5 * S, 8.5 * S, 7 * S);
    this.shell.position.set(0, 13.5 * S, 0.8 * S);
    this.shell.visible = false;
    this.shell.renderOrder = 45;
    this.disposables.push(sg, sm);

    const mkDecal = (mode: number, col: THREE.Color) => {
      const u = { uMode: { value: mode }, uK: { value: 0 }, uTime: time, uR: { value: 0.5 }, uArc: { value: 1.05 }, uCol: { value: col } };
      const g = new THREE.PlaneGeometry(1, 1);
      g.rotateX(-Math.PI / 2);
      const m = new THREE.ShaderMaterial({ vertexShader: DECAL_VERT, fragmentShader: DECAL_FRAG, uniforms: u, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, polygonOffset: true, polygonOffsetFactor: -4 });
      const mesh = new THREE.Mesh(g, m);
      mesh.visible = false; mesh.renderOrder = 30; mesh.frustumCulled = false;
      this.disposables.push(g, m);
      scene.add(mesh);
      return { mesh, u };
    };
    const danger = new THREE.Color(1.0, 0.28, 0.12);
    ({ mesh: this.lane, u: this.laneU } = mkDecal(0, danger));
    ({ mesh: this.ring, u: this.ringU } = mkDecal(1, danger));
    ({ mesh: this.fan, u: this.fanU } = mkDecal(2, new THREE.Color(1.0, 0.5, 0.12)));

    this.coreLight = new THREE.PointLight(0xff8a2a, 0, 26, 2);
    this.group.add(this.coreLight);
  }

  private onModel(root: THREE.Object3D) {
    root.scale.setScalar(S);
    const glowTex = makeGlowTex();
    this.disposables.push(glowTex);
    root.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      m.castShadow = true; m.receiveShadow = true;
      const mat = m.material as THREE.MeshStandardMaterial;
      if (!(mat.userData as { done?: boolean }).done) {
        mat.userData.done = true;
        if (mat.name === 'paint') { withHaze(mat, 0.35); this.paints.push(mat); }
        else withHaze(mat, 0.2);
        this.disposables.push(mat);
        if (mat.map) this.disposables.push(mat.map);
      }
      this.disposables.push(m.geometry);
    });
    this.model = root;
    this.body = root.getObjectByName('body') ?? null;
    for (const n of ['leg_L', 'leg_R']) { const l = root.getObjectByName(n); if (l) this.legs.push(l); }
    this.door = root.getObjectByName('furnace_door') ?? null;
    for (const k of HEAD_KEYS) {
      const head = root.getObjectByName('head_' + k) ?? null;
      const eye = (root.getObjectByName('fx_eye_' + k) as THREE.Mesh) ?? null;
      let eyeMat: THREE.MeshStandardMaterial | null = null;
      if (eye) { eyeMat = (eye.material as THREE.MeshStandardMaterial).clone(); eye.material = eyeMat; this.disposables.push(eyeMat); }
      const mouth = root.getObjectByName('sock_mouth_' + k) ?? null;
      const sm = new THREE.SpriteMaterial({ map: glowTex, color: 0xffc34a, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, opacity: 0 });
      const glow = new THREE.Sprite(sm);
      glow.scale.setScalar(4.2);
      glow.renderOrder = 46;
      if (mouth) mouth.add(glow);
      this.disposables.push(sm);
      const boom = root.getObjectByName('boom_' + k) ?? null, jaw = root.getObjectByName('jaw_' + k) ?? null;
      this.heads.push({
        boom, head, jaw, eye, eyeMat,
        eyeBase: eyeMat ? (eyeMat.emissive ?? new THREE.Color()).clone() : new THREE.Color(),
        mouth, glow, jawA: 0, rear: 0, droop: 0,
        boomRest: boom ? boom.rotation.clone() : new THREE.Euler(), headRest: head ? head.rotation.clone() : new THREE.Euler(), jawRest: jaw ? jaw.rotation.clone() : new THREE.Euler(),
      });
      this.weak.push(new THREE.Vector3());
    }
    this.weak.push(new THREE.Vector3()); // core
    this.group.add(root);
    this.group.add(this.shell);
  }

  /** the boss is gone from the snapshot; if it died, play the collapse there */
  died(time: number) {
    if (this.lastSeen < 0 || time - this.lastSeen > 1 || this.deadAt >= 0) return;
    this.deadAt = time;
    this.deadPose = { x: this.group.position.x, y: this.group.position.y, z: this.group.position.z, yaw: this.group.rotation.y };
  }

  /** null: no boss this frame */
  update(d: BossDraw | null, dt: number, time: number, ground: (x: number, z: number) => number) {
    this.lane.visible = this.ring.visible = this.fan.visible = false;
    if (!d) {
      if (this.deadAt >= 0 && this.deadPose) { this.collapse(time); return; }
      // hold the last pose a beat: the death event can land just after it leaves the snapshot
      if (this.lastSeen >= 0 && time - this.lastSeen < 1) return;
      this.group.visible = false; this.visibleAt = -1; this.lastSeen = -1; this.coreLight.intensity = 0;
      return;
    }
    this.lastSeen = time;
    this.deadAt = -1; this.deadPose = null;
    if (this.visibleAt < 0) this.visibleAt = time;
    this.group.visible = true;
    const n = d.net;
    // ---- root pose; the drop-in during the spawn state (st 5)
    let drop = 0;
    if (d.st === 5) { const k = Math.min(1, d.prog); drop = DROP_H * (1 - k * k); }
    const spd = Math.hypot(d.x - this.lx, d.z - this.lz) / Math.max(1e-3, dt);
    this.lx = d.x; this.lz = d.z;
    this.walk += dt * Math.min(2.2, spd * 0.5);
    const bob = Math.sin(this.walk * 2) * 0.25 * Math.min(1, spd / 2);
    this.group.position.set(d.x, d.y + drop, d.z);
    this.group.rotation.set(0, d.yaw + Math.PI, 0);
    if (!this.model) return;
    const stagger = d.st === 4;
    if (this.body) {
      this.body.position.y = 9 + bob + (stagger ? -1.2 : 0);
      this.body.rotation.x = stagger ? 0.12 : 0;
      this.body.rotation.z = Math.sin(this.walk) * 0.03;
    }
    this.legs.forEach((l, i) => { l.rotation.x = Math.sin(this.walk + i * Math.PI) * 0.12 * Math.min(1, spd / 2); });

    // ---- heads
    const tellHead = d.st === 1 ? ATK_HEAD[n.atk] : -2;
    const allTell = d.st === 1 && ATK_HEAD[n.atk] === -1;
    const execHead = d.st === 2 ? ATK_HEAD[n.atk] : -2;
    const k = 1 - Math.exp(-dt * 8);
    for (let i = 0; i < this.heads.length; i++) {
      const h = this.heads[i];
      const broken = (n.broken >> i) & 1, open = (n.mouths >> i) & 1;
      const rearT = !broken && (tellHead === i || allTell) ? (n.atk === 6 ? 0.5 : 0.35) : 0;
      const jawT = broken ? 0.9 : open ? 0.75 : execHead === i || (d.st === 2 && ATK_HEAD[n.atk] === -1) ? 0.6 : d.st === 1 && (tellHead === i || allTell) ? 0.25 : 0;
      h.rear += (rearT - h.rear) * k;
      h.jawA += (jawT - h.jawA) * (1 - Math.exp(-dt * 14));
      h.droop += ((broken ? 0.55 : 0) - h.droop) * k;
      const idle = Math.sin(time * 0.9 + i * 2.1) * 0.05;
      if (h.boom) { h.boom.rotation.x = h.boomRest.x - h.rear * 0.6 + h.droop * 0.5 + idle; h.boom.rotation.z = h.boomRest.z + Math.sin(time * 0.6 + i) * 0.03; }
      if (h.head) h.head.rotation.x = h.headRest.x - h.rear * 0.5 + h.droop + idle * 0.5;
      if (h.jaw) h.jaw.rotation.x = h.jawRest.x + h.jawA * 0.7;
      if (h.eyeMat) {
        const pulse = rearT > 0 ? 1.6 + 1.4 * Math.max(0, Math.sin(time * 18)) : 1;
        h.eyeMat.emissive.copy(h.eyeBase).multiplyScalar(broken ? 0.04 : pulse);
        h.eyeMat.color.setScalar(broken ? 0.15 : 1);
      }
      const gm = h.glow.material as THREE.SpriteMaterial;
      gm.opacity = open && !broken ? 0.65 + 0.35 * Math.sin(time * 7) : broken ? 0.18 : 0;
      gm.color.setHex(broken ? 0x9a5a2a : 0xffc34a);
      if (h.mouth) h.mouth.getWorldPosition(this.weak[i]);
    }
    // ---- furnace door + core
    if (this.door) this.door.rotation.x += ((n.core ? -1.3 : 0) - this.door.rotation.x) * k;
    this.coreLight.position.set(0, 9 * S, 3.2 * S);
    this.coreLight.intensity = n.core ? 60 + 30 * Math.sin(time * 9) : 0;
    // ---- hit flash on the plating
    const flash = d.hitAge < 0.12 ? 1 - d.hitAge / 0.12 : 0;
    for (const p of this.paints) p.emissive.setRGB(flash * 0.5, flash * 0.35, flash * 0.25);
    // ---- the Ruff
    const hyper = (n.phase & 8) !== 0;
    this.shellOn += ((hyper ? 1 : 0) - this.shellOn) * (1 - Math.exp(-dt * 5));
    this.shell.visible = this.shellOn > 0.01;
    this.shellU.uOn.value = this.shellOn;
    this.shellU.uCrack.value = n.crack;

    // ---- ground telegraphs
    const gy = ground(d.x, d.z);
    const fwdX = -Math.sin(d.yaw), fwdZ = -Math.cos(d.yaw);
    if (d.st === 1 || d.st === 2) {
      if (n.atk === 2) {
        // the lunge lane, boss → past the aim point
        const lx = d.aimX - d.x, lz = d.aimZ - d.z, l = Math.hypot(lx, lz) || 1;
        const run = Math.min(26, l + 5) + 4;
        const mx = d.x + (lx / l) * run / 2, mz = d.z + (lz / l) * run / 2;
        this.lane.visible = d.st === 1;
        this.lane.position.set(mx, ground(mx, mz) + 0.08, mz);
        this.lane.scale.set(9.2, 1, run);
        this.lane.rotation.set(0, Math.atan2(lx, lz), 0);
        this.laneU.uK.value = d.prog;
      } else if (n.atk === 1) {
        // the bark: a ring forming under it during the tell, rolling out during the attack
        const r = d.st === 1 ? 3 + d.prog : 2 + 24 * d.prog;
        this.ring.visible = true;
        this.ring.position.set(d.x, gy + 0.1, d.z);
        this.ring.scale.set(54, 1, 54);
        this.ringU.uR.value = r / 27;
      } else if (n.atk === 3) {
        // the scorch fan in front, 120° out to 18 m
        this.fan.visible = true;
        this.fan.position.set(d.x, gy + 0.09, d.z);
        this.fan.scale.set(36, 1, 36);
        this.fan.rotation.set(0, d.yaw, 0);
        this.fanU.uK.value = d.st === 1 ? d.prog : 1;
      } else if (n.atk === 6 && d.st === 1) {
        // the howl: a pulsing dome rim at its reach
        this.ring.visible = true;
        this.ring.position.set(d.x, gy + 0.1, d.z);
        this.ring.scale.set(140, 1, 140);
        this.ringU.uR.value = 0.98;
      }
    }
    void fwdX; void fwdZ;
  }

  private collapse(time: number) {
    const p = this.deadPose!;
    const age = time - this.deadAt;
    this.group.visible = age < 9;
    const f = Math.min(1, age / 2.4);
    this.group.position.set(p.x, p.y - Math.max(0, age - 4) * 3.5, p.z);
    this.group.rotation.set(f * f * 0.5, p.yaw, Math.sin(age * 3) * 0.03 * (1 - f));
    if (this.body) this.body.position.y = 9 - f * 3.2;
    for (const h of this.heads) {
      if (h.boom) h.boom.rotation.x = h.boomRest.x + f * 0.9;
      if (h.jaw) h.jaw.rotation.x = h.jawRest.x + 0.8;
      if (h.eyeMat) h.eyeMat.emissive.copy(h.eyeBase).multiplyScalar(Math.max(0, 1 - age * 0.8));
      (h.glow.material as THREE.SpriteMaterial).opacity = 0;
    }
    this.shell.visible = false;
    this.coreLight.intensity = age < 3 ? 80 * (1 - age / 3) : 0;
  }

  /** world point of the cab, for death FX */
  cabPos(out: THREE.Vector3) { return out.set(this.group.position.x, this.group.position.y + 10 * S, this.group.position.z); }

  dispose() {
    this.group.parent?.remove(this.group);
    for (const m of [this.lane, this.ring, this.fan]) m.parent?.remove(m);
    for (const d of this.disposables) d.dispose();
  }
}

function makeGlowTex() {
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const g = c.getContext('2d')!;
  const gr = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  gr.addColorStop(0, 'rgba(255,255,255,1)'); gr.addColorStop(0.3, 'rgba(255,255,255,0.5)'); gr.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = gr; g.fillRect(0, 0, 64, 64);
  const t = new THREE.CanvasTexture(c);
  return t;
}
